import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { TURNSTILE_SITE_KEY } from "@/lib/turnstile";
import { verifyTurnstile } from "@/lib/turnstile.functions";
import { toast } from "sonner";
import goblinHero from "@/assets/mascot-login-hero.png";

// Only allow same-origin relative paths to prevent open redirects.
function sanitizeRedirect(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  if (!value.startsWith("/")) return undefined;
  if (value.startsWith("//")) return undefined;
  if (value.startsWith("/\\")) return undefined;
  return value;
}

export const Route = createFileRoute("/")({
  component: IndexPage,
  validateSearch: (search: Record<string, unknown>): { redirect?: string | undefined } => ({
    redirect: sanitizeRedirect(search.redirect),
  }),
  head: () => ({
    meta: [
      { title: "Sign in — Bracket Goblin" },
      { name: "description", content: "Sign in to Bracket Goblin and run your bracket pool with bite." },
      { property: "og:title", content: "Sign in — Bracket Goblin" },
      { property: "og:description", content: "Sign in to Bracket Goblin and run your bracket pool with bite." },
      { property: "og:url", content: "https://bracketgoblin.com/" },
    ],
    links: [{ rel: "canonical", href: "https://bracketgoblin.com/" }],
  }),
});

function IndexPage() {
  const { redirect } = Route.useSearch();
  const { session, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const verifyFn = useServerFn(verifyTurnstile);

  // If a session already exists, send them to where they belong.
  // No global loading state — if there's no session yet, just show the form.
  useEffect(() => {
    if (session) {
      navigate({ to: redirect || (isAdmin ? "/admin" : "/home") });
    }
  }, [session, isAdmin, redirect, navigate]);

  // Hide the form briefly while we redirect an already-signed-in user
  // (avoids a flash of the login form for returning visitors).
  if (session) return null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const submittedEmail = String(formData.get("email") ?? "").trim();
    const submittedPassword = String(formData.get("password") ?? "");

    if (!submittedEmail || !submittedPassword) {
      const message = "Enter your email and password.";
      setErrorMessage(message);
      toast.error(message);
      return;
    }

    if (!turnstileToken) {
      const message = "Please complete the CAPTCHA challenge.";
      setErrorMessage(message);
      toast.error(message);
      return;
    }

    setErrorMessage(null);
    setSubmitting(true);
    try {
      // Verify CAPTCHA server-side first; tokens are single-use.
      try {
        await verifyFn({ data: { turnstileToken } });
      } catch (err) {
        const message = err instanceof Error ? err.message : "CAPTCHA verification failed.";
        setErrorMessage(message);
        toast.error(message);
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        return;
      }

      const { error } = await supabase.auth.signInWithPassword({
        email: submittedEmail,
        password: submittedPassword,
      });

      if (error) {
        setErrorMessage(error.message);
        toast.error(error.message);
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        void import("@/lib/analytics").then(({ track }) =>
          track("login_failure", {
            method: "password",
            reason: (error.message || "unknown").slice(0, 80),
          }),
        );
        return;
      }

      toast.success("Welcome back!");
      void import("@/lib/analytics").then(({ track }) =>
        track("login_success", { method: "password" }),
      );
    } catch {
      const message = "Sign-in failed. Please try again.";
      setErrorMessage(message);
      toast.error(message);
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    } finally {
      setSubmitting(false);
    }
    // The useEffect above handles navigation once session + isAdmin resolve
  };

  return (
    <main className="min-h-screen bg-background px-4 py-6 sm:py-10 flex items-center justify-center">
      <div className="w-full max-w-md flex flex-col items-center">
        {/* Wordmark */}
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight text-foreground text-center mb-1">
          Bracket <span className="text-primary">Goblin</span>
        </h1>
        <p className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-muted-foreground mb-6 text-center">
          Brackets Done Right
        </p>

        {/* Layered goblin + sign-in card */}
        <div className="relative w-full flex justify-center">
          <img
            src={goblinHero}
            alt="A grinning goblin mascot holding a tournament bracket"
            aria-hidden="true"
            width={450}
            height={800}
            fetchPriority="high"
            decoding="async"
            className="absolute left-1/2 -translate-x-1/2 w-[78%] max-w-[300px] h-auto select-none pointer-events-none z-0"
            style={{
              top: "-4%",
              filter: "drop-shadow(0 12px 32px oklch(0.78 0.16 138 / 0.25))",
            }}
          />

          <form
            method="post"
            action="/"
            onSubmit={handleSubmit}
            className="relative z-10 w-full max-w-sm space-y-4 rounded-xl border border-border bg-card p-6 shadow-[0_20px_50px_-12px_oklch(0_0_0/0.6)] mt-[55%]"
          >
            <div className="mb-2 text-center">
              <h2 className="font-display text-lg font-bold text-foreground">
                Sign in
              </h2>
            </div>
            {errorMessage ? (
              <div
                role="alert"
                className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {errorMessage}
              </div>
            ) : null}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="border-primary"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-primary"
              />
            </div>
            <div className="flex justify-center" style={{ minHeight: 65 }}>
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                options={{ theme: "dark" }}
                onSuccess={(token) => setTurnstileToken(token)}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
              />
            </div>
            <Button type="submit" className="w-full" disabled={submitting || !turnstileToken}>
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
            <div className="flex justify-end text-xs">
              <Link to="/forgot-password" className="text-muted-foreground hover:text-foreground">
                Forgot password?
              </Link>
            </div>
          </form>
        </div>

        <p className="relative z-10 mt-6 text-center text-xs text-muted-foreground">
          Have a league code?{" "}
          <Link to="/request-access" className="text-primary underline underline-offset-2">
            Request access
          </Link>
        </p>

      </div>
    </main>
  );
}
